/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: ACT and BeatAware sources on the report cards, built from
// the vendor results and plugin reviews the review snapshot carries on each
// recording. Platform code with no prototype template: upstream reads a
// provider-neutral evidence collection the viewer does not yet produce and
// targets cards by feature name; here the vendor-to-card map is explicit.
//
// A card lists a vendor source when at least one recording accepted for the
// card's evidence domain has a result from that vendor. The source is
// information for the reviewer: it never includes a card, and it never
// reaches the patient or provider documents. The decision on a vendor's
// result is the one recorded in the PCG viewer's plugin rack, per recording;
// the report shows it read-only. A source's `evidenceDisposition` is a
// per-card decision an earlier report panel recorded; documents keep it, but
// the panel no longer offers it.

import { actResult, beatAwareResult } from "../../pcg-viewer/plugins/vendor-results"
import { sourceIdForFeed } from "./features"

export const THIRD_PARTY_FEEDS = ["act", "beataware"]

const FEED_NAMES = { act: "ACT", beataware: "BeatAware" }

// Plain-language outcomes for a source's stored `evidenceDisposition`.
export const SOURCE_OUTCOME_LABELS = {
  supports: "Supports the report result",
  used_with_changes: "Use with changes",
  does_not_support: "Does not support the report result",
  unusable: "This evidence could not be assessed",
  not_used: "Do not use this evidence",
}

export const VIEWER_DECISION_NOT_REVIEWED = "Not reviewed in Viewer"

// The viewer's decision on one recording's vendor result, as the report
// states it: the rack's Accept reads as support for the report result,
// Reject as the opposite (a rejected result is not listed on a card, so the
// label is for documents that stored one); a note alone or no review is not
// a decision.
export function viewerDecisionOf(recording) {
  const status = recording && recording.viewerReview && recording.viewerReview.status
  if (status === "accepted") return { outcome: "supports", label: SOURCE_OUTCOME_LABELS.supports }
  if (status === "rejected") return { outcome: "does_not_support", label: SOURCE_OUTCOME_LABELS.does_not_support }
  return { outcome: null, label: VIEWER_DECISION_NOT_REVIEWED }
}

// Which vendor results inform which card, and from which of the rack's
// Result rows: ACT informs Structural Sound only; BeatAware informs the heart
// rate, the cycle-timing cards and Structural Sound. Cycle Regularity and
// Signal Quality have no vendor counterpart. A card lists the recordings
// accepted for its domain, plus any recording the reviewer accepted that
// vendor's result on in the rack — a result accepted in the viewer is shown
// even when the recording's own evidence was not accepted. A result the
// reviewer rejected is never listed. Rows keep the review's recording order.
const CARD_FEEDS = {
  "Heart Rate": { domain: "timing", feeds: { beataware: "Heart Rate" } },
  "SYS:DIA Ratio": { domain: "timing", feeds: { beataware: "Cycle Timing" } },
  "SYS:DIA Duration": { domain: "timing", feeds: { beataware: "Cycle Timing" } },
  "Structural Sound": {
    domain: "structuralSound",
    feeds: { act: "Vendor output", beataware: "Structural Sound" },
  },
}

const isThirdPartyFeed = (source) => THIRD_PARTY_FEEDS.includes(sourceIdForFeed(source))

// Whether a recording's evidence feeds the given domain of the report.
const feedsDomain = (recording, domain) => {
  if (!recording || recording.acceptedForExamRollup === false) return false
  const domains = recording.acceptedDomains
  return !domains || domains[domain] !== false
}

// The value one recording contributes to a card, or null without a result.
const recordingValue = (feed, rowLabel, recording) => {
  const evidence = recording.vendorEvidence || {}
  const result = feed === "act" ? actResult(evidence.act) : beatAwareResult(evidence.beataware)
  if (!result.available) return null
  const row = result.rows.find(([label]) => label === rowLabel)
  if (!row) return null
  if (feed === "act" && row[1] === "Unable to assess") return "Unable to assess (insufficient recording quality)"
  return row[1]
}

// The rack's decision on this vendor's result for the recording, if any
// (a note alone is not a decision)
const rackStatus = (recording, feed) => {
  const review = recording.pluginReviews && recording.pluginReviews[feed]
  return review && (review.status === "accepted" || review.status === "rejected") ? review.status : null
}

const viewerReviewOf = (recording, feed) => {
  const review = recording.pluginReviews && recording.pluginReviews[feed]
  return review ? { status: review.status, note: review.note || null } : null
}

// Rows a document saved before real vendor results carried: fixed demo
// findings with no recordings behind them. Removed wherever the report
// structure is normalized.
export function removePlaceholderThirdPartySources(features) {
  ;(features || []).forEach((feature) => {
    if (!feature || !Array.isArray(feature.sources)) return
    feature.sources = feature.sources.filter((source) => !isThirdPartyFeed(source) || source.thirdParty === true)
  })
}

// Replace every card's vendor sources with ones built from the report's
// recordings (the snapshot's accepted recordings, with their vendor results
// and plugin reviews).
export function attachThirdPartySources(report) {
  const recordings = Array.isArray(report.recordings) ? report.recordings : []
  ;(report.features || []).forEach((feature) => {
    if (!feature) return
    const kept = (feature.sources || []).filter((source) => !isThirdPartyFeed(source))
    const plan = CARD_FEEDS[feature.name]
    if (!plan) {
      if (Array.isArray(feature.sources)) feature.sources = kept
      return
    }
    const added = []
    THIRD_PARTY_FEEDS.forEach((feed) => {
      const rowLabel = plan.feeds[feed]
      if (!rowLabel) return
      const contributions = recordings
        .filter((recording) => rackStatus(recording, feed) !== "rejected")
        .filter((recording) => feedsDomain(recording, plan.domain) || rackStatus(recording, feed) === "accepted")
        .map((recording) => ({
          recordingId: recording.recordingId,
          position: recording.position || null,
          value: recordingValue(feed, rowLabel, recording),
          viewerReview: viewerReviewOf(recording, feed),
        }))
        .filter((contribution) => contribution.value !== null)
      if (!contributions.length) return
      added.push({
        sourceId: feed,
        sourceLabel: FEED_NAMES[feed],
        sourceType: "third_party_ai",
        name: FEED_NAMES[feed],
        thirdParty: true,
        finding: contributions
          .map((contribution) => `${contribution.position || "Recording"}: ${contribution.value}`)
          .join("; "),
        recordings: contributions,
        reviewStatus: "unreviewed",
      })
    })
    feature.sources = kept.concat(added)
  })
}
